import { FormEvent, useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import heroHeadshot from './assets/jon-1-turtleneck-3q.png';

type Motion = 'off' | 'subtle' | 'full';
type Status = 'idle' | 'sending' | 'sent' | 'error';
type HeroMetric =
  | { value: number; prefix: string; suffix: string; label: string }
  | { valueText: string; label: string };

const MOTION: Motion = 'full';

const JE = {
  ink: '#0A0A0C',
  ink2: '#111114',
  ink3: '#17171B',
  cream: '#F4EFE3',
  fg1: '#C8C2B4',
  fg2: '#8B8678',
  gold: '#C9A24A',
  goldSoft: '#D4B262',
  line: 'rgba(244,239,227,0.08)',
  lineStrong: 'rgba(244,239,227,0.16)',
};

function useInView(options: IntersectionObserverInit = {}) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -8% 0px', ...options },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, options.root, options.rootMargin, options.threshold]);

  return [setNode, inView] as const;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return reduced;
}

function useScrollY() {
  const [y, setY] = useState(0);

  useEffect(() => {
    const onScroll = () => setY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return y;
}

function HashScroller() {
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      const target = document.getElementById(decodeURIComponent(id));
      if (!target) return;
      const nav = document.querySelector('.je-nav');
      const navHeight = nav instanceof HTMLElement ? nav.offsetHeight : 72;
      const sectionCard = target.closest('.je-section-card');
      const transform = sectionCard instanceof HTMLElement ? getComputedStyle(sectionCard).transform : 'none';
      const cardTransform = transform === 'none' ? undefined : new DOMMatrixReadOnly(transform);
      const revealOffset = cardTransform && Number.isFinite(cardTransform.m42) ? cardTransform.m42 : 0;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - revealOffset - navHeight + 1);
      window.scrollTo({ top, behavior: 'auto' });
    };

    const settle = () => {
      window.requestAnimationFrame(scrollToHash);
      window.setTimeout(scrollToHash, 140);
      window.setTimeout(scrollToHash, 420);
    };

    settle();
    window.addEventListener('hashchange', settle);
    return () => window.removeEventListener('hashchange', settle);
  }, []);

  return null;
}

function CountUp({
  to,
  prefix = '',
  suffix = '',
  duration = 1600,
  start = true,
  delay = 0,
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  start?: boolean;
  delay?: number;
}) {
  const [setRef, inView] = useInView();
  const [value, setValue] = useState(0);
  const [compact, setCompact] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const query = window.matchMedia('(max-width: 900px)');
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!start || !inView) return undefined;
    if (reduced) {
      setValue(to);
      return undefined;
    }

    let raf = 0;
    let startedAt = 0;
    const runDuration = compact ? Math.max(duration, 6200) : Math.max(duration, 2200);
    const timer = window.setTimeout(() => {
      startedAt = performance.now();
      const tick = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / runDuration);
        const eased = compact ? 0.5 - Math.cos(progress * Math.PI) / 2 : 1 - Math.pow(1 - progress, 3);
        setValue(eased * to);
        if (progress < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, compact ? Math.min(delay, 260) : delay);

    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [compact, delay, duration, inView, reduced, start, to]);

  return (
    <span ref={setRef}>
      {prefix}
      {Math.round(value)}
      {suffix}
    </span>
  );
}

function JEParticleField({ intensity = 'full' }: { intensity?: Motion }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const reduced = useReducedMotion();
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 900px)');
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (intensity === 'off') return undefined;
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = window.innerWidth;
    let h = window.innerHeight;
    let raf = 0;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const pointCount = reduced ? 30 : compact ? 42 : intensity === 'subtle' ? 36 : 110;
    const speed = compact ? 0.74 : 1;
    const linkDistance = compact ? 24000 : 32000;
    const points = Array.from({ length: pointCount }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 1.2 * speed,
      vy: (Math.random() - 0.5) * 1.2 * speed,
      r: Math.random() * (compact ? 1.4 : 1.8) + 0.7,
    }));

    let t = 0;
    const draw = () => {
      t += compact ? 0.038 : 0.025;
      ctx.clearRect(0, 0, w, h);

      const glowX = w * (0.55 + (reduced ? 0 : 0.18 * Math.sin(t * 0.4)));
      const glowY = h * (0.45 + (reduced ? 0 : 0.12 * Math.cos(t * 0.3)));
      const gradient = ctx.createRadialGradient(glowX, glowY, 0, glowX, glowY, w * 0.4);
      gradient.addColorStop(0, compact ? 'rgba(201,162,74,0.2)' : 'rgba(201,162,74,0.22)');
      gradient.addColorStop(1, 'rgba(201,162,74,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      if (!reduced) {
        points.forEach((point) => {
          point.x += point.vx;
          point.y += point.vy;
          if (point.x < 0 || point.x > w) point.vx *= -1;
          if (point.y < 0 || point.y > h) point.vy *= -1;
        });
      }

      ctx.lineWidth = 0.7;
      for (let i = 0; i < pointCount; i += 1) {
        for (let j = i + 1; j < pointCount; j += 1) {
          const dx = points[i].x - points[j].x;
          const dy = points[i].y - points[j].y;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkDistance) {
            const alpha = (1 - d2 / linkDistance) * (compact ? 0.48 : 0.85);
            ctx.strokeStyle = `rgba(201,162,74,${alpha})`;
            ctx.beginPath();
            ctx.moveTo(points[i].x, points[i].y);
            ctx.lineTo(points[j].x, points[j].y);
            ctx.stroke();
          }
        }
      }

      points.forEach((point) => {
        ctx.fillStyle = compact ? 'rgba(244,239,227,0.72)' : 'rgba(244,239,227,1)';
        ctx.beginPath();
        ctx.arc(point.x, point.y, point.r, 0, Math.PI * 2);
        ctx.fill();
      });

      if (!reduced) raf = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [compact, intensity, reduced]);

  return <canvas className="je-particle-field" ref={ref} aria-hidden="true" />;
}

function JELogo({ animate = false }: { animate?: boolean }) {
  return (
    <span className={`je-primary-logo ${animate ? 'is-animated' : ''}`} aria-label="JE.Consulting">
      <span className="je-primary-logo-mark" aria-hidden="true">
        <svg viewBox="0 0 200 200" focusable="false">
          <defs>
            <linearGradient id="je-primary-mark" x1="0" y1="44" x2="0" y2="160" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#F7E4AA" />
              <stop offset="42%" stopColor="#DDB75F" />
              <stop offset="74%" stopColor="#C9A24A" />
              <stop offset="100%" stopColor="#8E6B26" />
            </linearGradient>
          </defs>
          <path
            d="M70 48L70 56L86 56L86 130Q86 152 70 158Q54 162 46 150L56 144Q60 152 68 150Q74 148 74 132L74 56L90 56L90 48Z"
            fill="url(#je-primary-mark)"
          />
          <path
            d="M100 48L158 48L158 60L116 60L116 96L148 96L148 108L116 108L116 144L158 144L158 156L100 156Z"
            fill="url(#je-primary-mark)"
          />
        </svg>
      </span>
      <span className="je-primary-logo-copy">
        <span className="je-primary-logo-word">
          JE<span>.consulting</span>
        </span>
        <span className="je-primary-logo-tag">COO · Transformation · AI</span>
      </span>
    </span>
  );
}

function JENav() {
  const y = useScrollY();
  const scrolled = y > 40;

  return (
    <nav
      className={`je-nav ${scrolled ? 'is-scrolled' : ''}`}
      style={{
        padding: scrolled ? '14px 56px' : '24px 56px',
        background: scrolled ? 'rgba(10,10,12,0.82)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        borderBottom: scrolled ? `1px solid ${JE.line}` : '1px solid transparent',
      }}
    >
      <a href="#top" className="je-nav-logo" aria-label="JE.Consulting home">
        <JELogo animate />
      </a>
      <div className="je-nav-links">
        <a href="#leadership">Leadership</a>
        <a href="#method">Method</a>
        <a href="#vendor-eval">Vendor eval</a>
        <a href="#ai-coaching">AI Coaching</a>
        <a href="#enquire">Contact</a>
      </div>
      <a href="#enquire" className="je-btn-primary je-nav-cta">
        Get in touch
      </a>
    </nav>
  );
}

function JEHero({ headshot, motion = 'full' }: { headshot: string; motion?: Motion }) {
  const scrollY = useScrollY();
  const reduced = useReducedMotion();
  const [reveal, setReveal] = useState(false);
  const portraitTransform = motion === 'off' || reduced ? undefined : `translateY(${scrollY * 0.15}px)`;
  const headline = ['Strategy', 'meets', 'systems'];

  useEffect(() => {
    const timer = window.setTimeout(() => setReveal(true), 100);
    return () => window.clearTimeout(timer);
  }, []);

  const goToSection = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    event.preventDefault();

    const id = href.replace('#', '');
    const target = document.getElementById(id);
    if (!target) {
      window.location.hash = id;
      return;
    }

    const nav = document.querySelector('.je-nav');
    const navHeight = nav instanceof HTMLElement ? nav.offsetHeight : 0;
    const sectionCard = target.closest('.je-section-card');
    const transform = sectionCard instanceof HTMLElement ? getComputedStyle(sectionCard).transform : 'none';
    const cardTransform = transform === 'none' ? undefined : new DOMMatrixReadOnly(transform);
    const revealOffset = cardTransform && Number.isFinite(cardTransform.m42) ? cardTransform.m42 : 0;
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - revealOffset - navHeight + 1);

    window.history.pushState(null, '', href);
    window.scrollTo({
      top,
      behavior: motion === 'off' || reduced ? 'auto' : 'smooth',
    });
  };

  const metrics: HeroMetric[] = [
    { value: 250, prefix: '$', suffix: 'm', label: 'Cost saved' },
    { value: 400, prefix: '$', suffix: 'm', label: 'OpEx unlocked' },
    { value: 20, prefix: '', suffix: '%', label: 'Profit uplift' },
    { valueText: 'Global', label: '53 market rollouts' },
  ];
  const propositions = [
    {
      n: '01',
      title: 'Leadership',
      copy: 'COO / Transformation Director.',
      href: '#leadership',
    },
    {
      n: '02',
      title: 'Change that holds',
      copy: (
        <>
          Advisory - Strategy and op model design.
          <br />
          Delivery - own, review or rescue.
        </>
      ),
      href: '#method',
    },
    {
      n: '03',
      title: 'Expert in your corner',
      copy: 'AI vendor & systems evaluation.',
      href: '#vendor-eval',
    },
    {
      n: '04',
      title: 'AI Coaching',
      copy: 'Executive & team coaching.',
      href: '#ai-coaching',
    },
  ];

  return (
    <section id="top" className="je-hero">
      <div className="je-hero-glow" />
      <div className="je-hero-layout">
        <div className="je-hero-left">
          <div>
            <div
              className="je-hero-eyebrow"
              style={{
                opacity: reveal ? 1 : 0,
                transform: reveal ? 'translateY(0)' : 'translateY(8px)',
              }}
            >
              <span style={{ width: reveal ? 56 : 0 }} />
              <strong>Independent advisory</strong>
            </div>

            <h1 className="je-hero-title">
              {headline.map((word, index) => (
                <span
                  key={word}
                  style={{
                    opacity: reveal ? 1 : 0,
                    transform: reveal ? 'translateY(0)' : 'translateY(40px)',
                    transition: `all 1000ms ${300 + index * 150}ms cubic-bezier(0.22,1,0.36,1)`,
                    color: index === 2 ? JE.gold : JE.cream,
                    fontWeight: index === 2 ? 600 : 500,
                  }}
                >
                  {word}
                </span>
              ))}
            </h1>

            <p
              className="je-hero-copy"
              style={{
                opacity: reveal ? 1 : 0,
                transform: reveal ? 'translateY(0)' : 'translateY(20px)',
              }}
            >
              Senior operator expertise built over 16 years across operations, consulting, and transformation;
              working with FTSE 100 and SME businesses, specialising in financial services and related industries.
            </p>

            <div
              className="je-mobile-portrait"
              style={{
                opacity: reveal ? 1 : 0,
                transform: reveal ? 'translateY(0)' : 'translateY(22px)',
              }}
            >
              <img src={headshot} alt="Jon Edgar" />
              <div>
                <strong>Jon Edgar</strong>
                <span>Principal / United Kingdom</span>
              </div>
            </div>

            <div
              className="je-hero-proposition"
              style={{
                opacity: reveal ? 1 : 0,
                transform: reveal ? 'translateY(0)' : 'translateY(18px)',
              }}
            >
              {propositions.map((item) => (
                <a key={item.n} href={item.href} onClick={(event) => goToSection(event, item.href)}>
                  <span>{item.n}</span>
                  <h2>{item.title}</h2>
                  <p>{item.copy}</p>
                </a>
              ))}
            </div>

            <div
              className="je-hero-actions"
              style={{
                opacity: reveal ? 1 : 0,
                transform: reveal ? 'translateY(0)' : 'translateY(20px)',
              }}
            >
              <a href="#enquire" className="je-btn-primary">
                Get in touch
              </a>
              <a href="#method" className="je-btn-secondary">
                View method
              </a>
            </div>
          </div>

          <div className="je-hero-metrics" style={{ opacity: reveal ? 1 : 0 }}>
            {metrics.map((metric, index) => (
              <div key={metric.label}>
                <strong>
                  {'value' in metric ? (
                    <CountUp
                      to={metric.value}
                      prefix={metric.prefix}
                      suffix={metric.suffix}
                      start={reveal}
                      delay={1700 + index * 150}
                      duration={1600}
                    />
                  ) : (
                    metric.valueText
                  )}
                </strong>
                <span>{metric.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="je-hero-portrait">
          <div
            className="je-hero-portrait-inner"
            style={{
              transform: portraitTransform,
              transition: reduced ? undefined : 'transform 80ms linear',
            }}
          >
            <img
              src={headshot}
              alt="Jon Edgar"
              style={{
                clipPath: reveal ? 'inset(0 0 0 0)' : 'inset(0 0 100% 0)',
              }}
            />
          </div>
          <div className="je-portrait-vignette-one" />
          <div className="je-portrait-vignette-two" />
          <div className="je-portrait-vignette-three" />
          <div className="je-corner je-corner-top" style={{ opacity: reveal ? 1 : 0 }} />
          <div className="je-corner je-corner-bottom" style={{ opacity: reveal ? 1 : 0 }} />
          <div
            className="je-portrait-caption"
            style={{
              opacity: reveal ? 1 : 0,
              transform: reveal ? 'translateY(0)' : 'translateY(12px)',
            }}
          >
            <strong>Jon Edgar</strong>
            <span>Principal / United Kingdom</span>
          </div>
        </div>
      </div>

      <div className="je-scroll-indicator" style={{ opacity: reveal ? 1 : 0 }}>
        <span>Scroll</span>
        <span />
      </div>
    </section>
  );
}

function SectionCard({ children, delay = 0 }: { children: ReactNode; delay?: number }) {
  const [setRef, inView] = useInView({ threshold: 0.12 });

  return (
    <div
      ref={setRef}
      className="je-section-card"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.985)',
        transition: `opacity 1100ms ${delay}ms cubic-bezier(0.22,1,0.36,1), transform 1100ms ${delay}ms cubic-bezier(0.22,1,0.36,1)`,
      }}
    >
      {children}
    </div>
  );
}

type BuyerVisualKind = 'cadence' | 'rescue' | 'score' | 'coaching';

function BuyerVisual({ kind }: { kind: BuyerVisualKind }) {
  const reduced = useReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduced) return undefined;
    let raf = 0;
    const start = performance.now();
    const run = (now: number) => {
      setTick((now - start) / 720);
      raf = requestAnimationFrame(run);
    };
    raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  const t = reduced ? 0.7 : tick;
  const wavePoints = Array.from({ length: 28 })
    .map((_, index) => {
      const x = 24 + index * 11.5;
      const y = 76 + 18 * Math.sin(index * 0.55 + t * 1.5);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="je-buyer-visual">
      <svg viewBox="0 0 360 150" aria-hidden="true">
        {kind === 'cadence' && (
          <g>
            {[0, 1, 2].map((index) => (
              <rect
                className="je-artifact-box"
                key={index}
                x={42 + index * 104}
                y={42}
                width="62"
                height="42"
                rx="4"
                opacity={0.42 + 0.48 * Math.abs(Math.sin(t * 0.8 + index))}
              />
            ))}
            <path className="je-artifact-flow" d="M104 63 L146 63 M208 63 L250 63" />
            {Array.from({ length: 5 }).map((_, index) => (
              <g key={index} transform={`translate(${58 + index * 58} 108)`}>
                <rect className="je-artifact-track" width="30" height="7" rx="2" />
                <rect className="je-artifact-fill" width={10 + 18 * Math.abs(Math.sin(t + index))} height="7" rx="2" />
              </g>
            ))}
          </g>
        )}
        {kind === 'rescue' && (
          <g>
            {[0, 1, 2, 3].map((index) => (
              <g key={index} transform={`translate(38 ${34 + index * 24})`}>
                <circle className={index < 2 ? 'je-artifact-risk' : 'je-artifact-dot'} cx="6" cy="6" r="4" />
                <rect className="je-artifact-track" x="22" y="2" width="230" height="8" rx="2" />
                <rect
                  className="je-artifact-fill"
                  x="22"
                  y="2"
                  width={78 + index * 32 + 18 * Math.abs(Math.sin(t * 0.8 + index))}
                  height="8"
                  rx="2"
                />
                <text x="272" y="9">{index === 0 ? 'reset' : index === 1 ? 'risk' : index === 2 ? 'plan' : 'benefit'}</text>
              </g>
            ))}
          </g>
        )}
        {kind === 'score' && (
          <g>
            {['FIT', 'DATA', 'COST', 'RISK', 'ADOPT'].map((label, index) => (
              <text key={label} x={54 + index * 56} y="28">{label}</text>
            ))}
            {[0, 1, 2].map((row) => (
              <g key={row} transform={`translate(26 ${52 + row * 34})`}>
                <text x="0" y="9">V{row + 1}</text>
                {[0, 1, 2, 3, 4].map((col) => (
                  <rect
                    className={row === 0 ? 'je-artifact-fill' : 'je-artifact-track'}
                    key={col}
                    x={52 + col * 56}
                    y="1"
                    width={18 + 26 * Math.abs(Math.sin(t * 0.7 + row + col))}
                    height="7"
                    rx="2"
                  />
                ))}
              </g>
            ))}
            <path className="je-artifact-cursor" d={`M${238 + 18 * Math.sin(t)},${96 + 12 * Math.cos(t)} l19 7 l-8 5 l-5 17 z`} />
          </g>
        )}
        {kind === 'coaching' && (
          <g>
            <polyline className="je-artifact-wave" points={wavePoints} />
            {['BUY', 'BUILD', 'STOP', 'GOVERN'].map((label, index) => (
              <g key={label} transform={`translate(${44 + index * 72} ${78 + 24 * Math.sin(t * 0.65 + index)})`}>
                <circle className="je-artifact-dot" r="5" />
                <text x="-18" y="26">{label}</text>
              </g>
            ))}
            <line className="je-artifact-base" x1="24" y1="76" x2="336" y2="76" />
          </g>
        )}
      </svg>
    </div>
  );
}

const leadershipProofs = [
  {
    tag: 'Op model',
    title: 'Transformation operating model',
    metric: '$250m savings',
    body:
      'Redesigned transformation operating models and designed Operational Resilience models for a multi-national bank, improving oversight and enabling annualised savings.',
  },
  {
    tag: 'AI into systems',
    title: 'AI expenses',
    metric: '$15m savings',
    body:
      'Standardised expenses, payments and approval authorities, using AI to route and auto-approve, improve spend visibility and remove manual processing.',
  },
  {
    tag: 'Retail transformation',
    title: 'Customer system',
    metric: '20% revenue growth',
    body:
      'Designed and implemented a customer-experience approach into the retail network, moving to the most engaged and efficient colleagues in the industry and improving customer satisfaction metrics.',
  },
  {
    tag: 'RFP',
    title: 'Procurement centralisation',
    metric: '$400m OpEx',
    body:
      'Led a $1.7b procurement centralisation programme, reducing third-party vendors by 97% and OpEx by $400m across 7 value streams while managing the onboarding of new vendors.',
  },
  {
    tag: 'Compliance',
    title: 'RegTech platform',
    metric: '$45m budget',
    body:
      'Led 80+ people on a $45m RegTech platform, delivering an MVP in 6 months and five systems with an average 12-month full rollout per system.',
  },
  {
    tag: 'COO Office',
    title: 'Annual operating plan',
    metric: '$280m AOP',
    body:
      'Responsible for the AOP, performance targets and cost management. Designed the 5-year roadmap and took it through global stakeholder signoff and adoption.',
  },
];

function JEProofCycle() {
  const [active, setActive] = useState(0);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % leadershipProofs.length);
    }, 3600);
    return () => window.clearInterval(timer);
  }, [reduced]);

  const selected = leadershipProofs[active];
  const nextProof = () => setActive((current) => (current + 1) % leadershipProofs.length);
  const previousProof = () => setActive((current) => (current + leadershipProofs.length - 1) % leadershipProofs.length);

  return (
    <article className="je-leadership-concept je-proof-cycle-card" id="outcomes">
      <div className="je-proof-cycle-hero" key={selected.title}>
        <small>{selected.tag}</small>
        <strong>{selected.title}</strong>
        <h3>{selected.metric}</h3>
        <p>{selected.body}</p>
        <div className="je-proof-carousel-controls">
          <button type="button" onClick={previousProof} aria-label="Previous leadership proof">
            Prev
          </button>
          <span>
            {active + 1} / {leadershipProofs.length}
          </span>
          <button type="button" onClick={nextProof} aria-label="Next leadership proof">
            Next
          </button>
        </div>
      </div>
      <div className="je-proof-cycle-list" aria-label="Leadership proof examples">
        {leadershipProofs.map((proof, index) => (
          <button
            type="button"
            key={proof.title}
            className={index === active ? 'is-active' : undefined}
            onClick={() => setActive(index)}
          >
            <span>{proof.tag}</span>
            <strong>{proof.title}</strong>
            <em>{proof.metric}</em>
          </button>
        ))}
      </div>
      <div className="je-proof-pattern">
        <strong>The pattern</strong>
        <p>
          Senior ownership of complex cross-functional work: lock the operating model, route AI into real workflow,
          challenge the RFP, carry people through adoption, and keep cost, risk and performance moving until the numbers
          show up.
        </p>
      </div>
    </article>
  );
}

function JELeadership() {
  return (
    <section id="leadership" className="je-section je-section-muted je-section-leadership">
      <span id="approach" className="je-anchor-offset" aria-hidden="true" />
      <div className="je-container je-buyer-grid">
        <div className="je-buyer-header">
          <div>
            <div className="je-kicker">Leadership</div>
            <h2 className="je-section-title">
              Legacy operations to
              <br />
              <em>AI-enabled operating systems</em>
            </h2>
          </div>
          <p>
            Most organisations are not short on AI ambition. They are short on the operating rhythm that turns it into
            outcomes, and the people strategy that drives adoption.
          </p>
        </div>

        <JEProofCycle />
      </div>
    </section>
  );
}

function JELeadershipLegacy() {
  const roles = [
    {
      label: '01',
      title: 'COO',
      text:
        'When the business needs an operator inside the system: clear priorities, better executive decisions, performance discipline, and the cadence to make work move.',
      bullets: ['Operating rhythm', 'Executive decisions', 'Performance discipline', 'AI into daily work'],
    },
    {
      label: '02',
      title: 'Transformation Director',
      text:
        'When a programme matters enough to need ownership: sequence the work, unblock delivery, recover drift, and prove the benefits before confidence disappears.',
      bullets: ['Strategy into plan', 'Operating model design', 'Programme rescue', 'Benefits control'],
    },
  ];
  const proof = [
    ['$400m', 'OpEx unlocked through a 53-market operating-model rollout.'],
    ['$250m', 'Cost savings delivered across global transformation programmes.'],
    ['20%', 'Profit uplift from operating discipline, accountability, and delivery focus.'],
    ['$15m', 'AI-enabled savings shipped through practical systems in production.'],
  ];

  return (
    <section className="je-section je-section-muted je-section-leadership">
      <div className="je-container je-leadership-grid">
        <div className="je-leadership-intro">
          <div className="je-kicker">Leadership</div>
          <h2 className="je-section-title">
            COO.
            <br />
            <em>Transformation Director.</em>
          </h2>
          <p>
            Hire me when the work is too important to leave between functions: strategy has to become operating
            rhythm, decisions need pace, and delivery needs someone accountable for the outcome.
          </p>
        </div>
        <div className="je-leadership-split">
          {roles.map((role) => (
            <article key={role.title} className="je-leadership-role">
              <span>{role.label}</span>
              <h3>{role.title}</h3>
              <p>{role.text}</p>
              <div>
                {role.bullets.map((bullet) => (
                  <strong key={bullet}>{bullet}</strong>
                ))}
              </div>
            </article>
          ))}
        </div>
        <div className="je-leadership-proof">
          <div>
            <span>Proof</span>
            <h3>Outcomes that survive scrutiny.</h3>
            <p>
              The reason to hire me is not another operating framework. It is a track record of turning complex work
              into measurable outcomes, with enough structure left behind for the business to keep moving.
            </p>
          </div>
          <div className="je-leadership-proof-list">
            {proof.map(([metric, text]) => (
              <article key={metric}>
                <strong>{metric}</strong>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function OpModelNetwork({ intensity = 'full' }: { intensity?: Motion }) {
  const [time, setTime] = useState(0);
  const [compact, setCompact] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const query = window.matchMedia('(max-width: 900px)');
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (intensity === 'off' || reduced) return undefined;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      setTime(((now - start) / 1000) * (compact ? 1.35 : 0.9));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [compact, intensity, reduced]);

  const nodes = [
    { id: 'board', x: 0.1, y: 0.18, label: 'Board' },
    { id: 'strategy', x: 0.3, y: 0.1, label: 'Strategy', accent: true },
    { id: 'opmodel', x: 0.5, y: 0.42, label: 'Operating model', big: true },
    { id: 'vendor', x: 0.86, y: 0.2, label: 'AI vendors' },
    { id: 'system', x: 0.82, y: 0.55, label: 'AI systems' },
    { id: 'delivery', x: 0.3, y: 0.65, label: 'Delivery' },
    { id: 'coaching', x: 0.1, y: 0.5, label: 'Coaching' },
    { id: 'outcome', x: 0.55, y: 0.88, label: 'Outcomes', accent: true },
  ];

  const edges = [
    ['board', 'strategy'],
    ['strategy', 'opmodel', 'core'],
    ['board', 'opmodel'],
    ['opmodel', 'vendor'],
    ['opmodel', 'system'],
    ['vendor', 'system'],
    ['opmodel', 'delivery'],
    ['delivery', 'outcome'],
    ['opmodel', 'outcome', 'core'],
    ['system', 'outcome'],
    ['coaching', 'opmodel'],
    ['coaching', 'board'],
    ['delivery', 'system'],
  ];

  const animatedNodes = nodes.map((node, index) => ({
    ...node,
    x: node.x + (reduced ? 0 : Math.sin(time * 0.32 + index) * (compact ? 0.006 : 0.004)),
    y: node.y + (reduced ? 0 : Math.cos(time * 0.38 + index * 1.3) * (compact ? 0.006 : 0.004)),
  }));
  const byId = Object.fromEntries(animatedNodes.map((node) => [node.id, node]));
  const original = Object.fromEntries(nodes.map((node) => [node.id, node]));
  const activeEdges = new Set([
    'board-strategy',
    'strategy-opmodel',
    'opmodel-vendor',
    'opmodel-system',
    'opmodel-delivery',
    'delivery-outcome',
    'opmodel-outcome',
    'coaching-opmodel',
  ]);

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className={`je-network-svg${compact ? ' is-compact' : ''}`}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="je-node-glow">
          <stop offset="0%" stopColor={JE.gold} stopOpacity="0.3" />
          <stop offset="100%" stopColor={JE.gold} stopOpacity="0" />
        </radialGradient>
      </defs>
      {edges.map(([a, b, kind], index) => {
        const start = byId[a];
        const end = byId[b];
        const core = kind === 'core';
        const key = `${a}-${b}`;
        const active = activeEdges.has(key);
        const phase = (time * (compact ? 0.55 : 0.38) + index * 0.16) % 1;
        return (
          <g key={`${a}-${b}`}>
            <line
              className={`je-network-link-base${core ? ' is-core' : ''}`}
              x1={start.x * 100}
              y1={start.y * 100}
              x2={end.x * 100}
              y2={end.y * 100}
              stroke={core ? JE.gold : JE.fg2}
              strokeOpacity={core ? (compact ? '0.82' : '0.62') : compact ? '0.48' : '0.35'}
              strokeWidth={core ? (compact ? '0.36' : '0.24') : compact ? '0.22' : '0.15'}
            />
            {!reduced && active && (
              <>
                <line
                  className={`je-network-link-flow${core ? ' is-core' : ''}`}
                  x1={start.x * 100}
                  y1={start.y * 100}
                  x2={end.x * 100}
                  y2={end.y * 100}
                  style={{ animationDelay: `${-index * 0.18}s` }}
                />
                <circle
                  className="je-network-packet je-network-packet-primary"
                  cx={start.x * 100 + (end.x - start.x) * 100 * phase}
                  cy={start.y * 100 + (end.y - start.y) * 100 * phase}
                  r={compact ? '0.86' : '0.46'}
                  fill={JE.gold}
                  opacity={core ? '0.92' : '0.72'}
                />
              </>
            )}
          </g>
        );
      })}
      {animatedNodes.map((node, index) => {
        const source = original[node.id];
        const pulse = 1 + (reduced ? 0 : 0.08 * Math.sin(time * 0.9 + index));
        const big = Boolean(source.big);
        const accent = Boolean(source.accent);
        const coreNode = big || accent;
        return (
          <g key={node.id}>
            <circle cx={node.x * 100} cy={node.y * 100} r={big ? 9 : 6} fill="url(#je-node-glow)" />
            <circle
              className={`je-network-node${coreNode ? ' is-core' : ''}`}
              cx={node.x * 100}
              cy={node.y * 100}
              r={(big ? 2.55 : accent ? 2.05 : 1.55) * pulse}
              fill={accent || big ? JE.gold : JE.cream}
              stroke={JE.gold}
              strokeWidth="0.25"
            />
            <text
              x={node.x * 100}
              y={node.y * 100 + (big ? 5.5 : 4.6)}
              textAnchor="middle"
              fill={accent || big ? JE.gold : JE.cream}
              fontSize={coreNode ? 2.6 : 2.2}
              fontFamily="Inter, sans-serif"
              fontWeight={coreNode ? 600 : 500}
            >
              {node.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function JEOpModel({ motion = 'full' }: { motion?: Motion }) {
  const [setRef, inView] = useInView();
  const steps = [
    'Diagnose the operating system',
    'Sequence the moves that matter',
    'Stand up the cadence that holds',
    'Hand back stronger than found',
  ];

  return (
    <section id="method" className="je-section je-section-muted">
      <div className="je-container je-op-grid">
        <div ref={setRef}>
          <div className="je-kicker">Method</div>
          <h2 className="je-section-title">
            The system
            <br />
            <em>is the strategy</em>
          </h2>
          <p>
            First, we help define, test, and lock the strategy. Then we build the operating model that carries it:
            decisions moving cleanly, work sequenced properly, and AI vendors and systems evaluated against the way
            the business actually runs.
          </p>
          <p>
            That is the COO lens: not just what should happen, but who owns it, how it runs, where it breaks, and how
            people adopt it.
          </p>
          <p>
            From there, every engagement becomes a map: board to outcome, strategy to delivery, vendor to system.
            The smallest set of moves that will change the organisation, and the cadence to keep it moving after the
            engagement.
          </p>
          <div className="je-step-list">
            {steps.map((step, index) => (
              <div
                key={step}
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateX(0)' : 'translateX(-12px)',
                  transition: `all 800ms ${index * 120}ms cubic-bezier(0.22,1,0.36,1)`,
                }}
              >
                <span>0{index + 1}</span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="je-network-panel">
          <div className="je-network-motion-layer" aria-hidden="true" />
          <OpModelNetwork intensity={motion} />
          <span>The operating system</span>
        </div>
      </div>
    </section>
  );
}

function PillarCard({
  n,
  title,
  blurb,
  bullets,
  targetId,
}: {
  n: string;
  title: string;
  blurb: string;
  bullets: string[];
  targetId: string;
}) {
  const [hover, setHover] = useState(false);

  return (
    <article
      id={targetId}
      className="je-pillar-card"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ background: hover ? JE.ink3 : JE.ink2 }}
    >
      <div className="je-pillar-rule" style={{ width: hover ? '100%' : 40 }} />
      <div className="je-pillar-heading">
        <span>{n}</span>
        <h3>{title}</h3>
      </div>
      <p>{blurb}</p>
      <div className="je-pillar-bullets">
        {bullets.map((bullet) => (
          <span key={bullet}>
            <i />
            {bullet}
          </span>
        ))}
      </div>
    </article>
  );
}

function JEPillars() {
  const engagementModes = [
    {
      label: 'Leadership',
      value: 'COO / Transformation Director',
    },
    {
      label: 'Advisory',
      value: 'Strategy / op model / delivery',
    },
    {
      label: 'AI evaluation',
      value: 'Vendor & systems',
    },
    {
      label: 'AI coaching',
      value: 'Executive & team',
    },
  ];

  const pillars = [
    {
      n: '01',
      title: 'Leadership',
      blurb:
        'COO or Transformation Director support when the business needs senior operating ownership: rhythm, priorities, performance, and delivery.',
      bullets: ['COO mandate', 'Transformation Director', 'Operating cadence'],
    },
    {
      n: '02',
      title: 'Advisory',
      blurb:
        'Strategy, operating model, and delivery advisory when you need the system designed, challenged, sequenced, or recovered.',
      bullets: ['Strategy lock', 'Operating model design', 'Delivery recovery'],
    },
    {
      n: '03',
      title: 'AI evaluation',
      blurb:
        "Vendor and systems evaluation for AI platforms, agents, and tooling. Claims tested. Architecture challenged. Operating fit made clear.",
      bullets: ['Vendor scorecards', 'Architecture review', 'Buy / build / wait'],
    },
    {
      n: '04',
      title: 'AI coaching',
      blurb:
        'Executive and team coaching for sharper AI judgement: what to buy, what to build, what to stop, and how to lead the operating change.',
      bullets: ['Executive coaching', 'Team sessions', 'Operating change'],
    },
  ];

  return (
    <section id="approach" className="je-section">
      <div className="je-container">
        <div className="je-section-intro">
          <div className="je-kicker">Practice</div>
          <h2 className="je-section-title">
            How people
            <br />
            hire me.
          </h2>
          <div className="je-engagement-modes">
            {engagementModes.map((mode) => (
              <div key={mode.label}>
                <span>{mode.label}</span>
                <strong>{mode.value}</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="je-pillars-grid">
          {pillars.map((pillar) => (
            <PillarCard key={pillar.n} targetId={`practice-${pillar.n}`} {...pillar} />
          ))}
        </div>
      </div>
    </section>
  );
}

function JEVendorEval({ motion = 'full' }: { motion?: Motion }) {
  const [time, setTime] = useState(0);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (motion === 'off' || reduced) return undefined;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      setTime((now - start) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [motion, reduced]);

  const vendors = ['Vendor A', 'Vendor B', 'Vendor C', 'Vendor D'];
  const dims = ['Fit', 'Data', 'Cost', 'Risk', 'Stack', 'Adopt'];
  const scores = [
    [0.88, 0.78, 0.62, 0.81, 0.74, 0.86],
    [0.55, 0.48, 0.91, 0.45, 0.58, 0.52],
    [0.71, 0.68, 0.48, 0.66, 0.82, 0.64],
    [0.42, 0.53, 0.55, 0.71, 0.39, 0.47],
  ];

  const totals = scores.map((vendorScores) =>
    Math.round((vendorScores.reduce((sum, score) => sum + score, 0) / vendorScores.length) * 100),
  );

  return (
    <>
      <svg viewBox="0 0 520 460" className="je-vendor-svg" aria-hidden="true">
        <defs>
          <linearGradient id="je-gold-fill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#9C7A30" />
            <stop offset="100%" stopColor="#D4B262" />
          </linearGradient>
        </defs>
        <text x="24" y="26" fill={JE.gold} fontSize="10" fontFamily="JetBrains Mono, monospace" letterSpacing="2">
          FIG. 02 - VENDOR EVALUATION
        </text>
        <line x1="24" y1="36" x2="496" y2="36" stroke={JE.lineStrong} strokeWidth="0.5" />
        {dims.map((dimension, index) => (
          <text
            key={dimension}
            x={190 + index * 52}
            y="66"
            fill={JE.fg2}
            fontSize="8.5"
            fontFamily="Inter, sans-serif"
            letterSpacing="1.2"
            textAnchor="middle"
          >
            {dimension.toUpperCase()}
          </text>
        ))}

        {vendors.map((vendor, vendorIndex) => {
          const y = 80 + vendorIndex * 70;
          const winner = vendorIndex === 0;
          return (
            <g key={vendor}>
              <rect
                x="24"
                y={y}
                width="472"
                height="56"
                rx="3"
                fill={winner ? 'rgba(201,162,74,0.06)' : 'rgba(244,239,227,0.02)'}
                stroke={winner ? JE.gold : JE.line}
                strokeWidth={winner ? 0.8 : 0.5}
              />
              <line x1="152" y1={y} x2="152" y2={y + 56} stroke={winner ? 'rgba(201,162,74,0.35)' : JE.line} />
              <text
                x="44"
                y={y + 24}
                fill={winner ? JE.gold : JE.cream}
                fontFamily="Outfit, Inter, sans-serif"
                fontSize="15"
                fontWeight="500"
              >
                {vendor}
              </text>
              <text x="44" y={y + 40} fill={JE.fg2} fontSize="8" fontFamily="Inter" letterSpacing="1.2">
                {winner ? 'RECOMMENDED' : 'EVALUATING'}
              </text>
              {scores[vendorIndex].map((scoreBase, dimIndex) => {
                const pulse = 1 + (reduced ? 0 : 0.04 * Math.sin(time * 1.5 + vendorIndex + dimIndex));
                const score = Math.min(1, scoreBase * pulse);
                const cx = 190 + dimIndex * 52;
                return (
                  <g key={`${vendor}-${dims[dimIndex]}`}>
                    <line
                      x1={cx - 16}
                      y1={y + 30}
                      x2={cx + 16}
                      y2={y + 30}
                      stroke="rgba(244,239,227,0.1)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <line
                      x1={cx - 16}
                      y1={y + 30}
                      x2={cx - 16 + 32 * score}
                      y2={y + 30}
                      stroke={
                        score > 0.75 ? JE.gold : score > 0.5 ? 'rgba(201,162,74,0.65)' : 'rgba(244,239,227,0.35)'
                      }
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <text
                      x={cx}
                      y={y + 18}
                      fill={score > 0.75 ? JE.gold : JE.fg2}
                      fontSize="9"
                      fontFamily="JetBrains Mono, monospace"
                      textAnchor="middle"
                    >
                      {(score * 100).toFixed(0)}
                    </text>
                  </g>
                );
              })}
              {winner && !reduced && (
                <line
                  x1={24 + ((time * 92) % 472)}
                  y1={y}
                  x2={24 + ((time * 92) % 472)}
                  y2={y + 56}
                  stroke={JE.gold}
                  strokeWidth="0.5"
                  opacity="0.4"
                />
              )}
            </g>
          );
        })}
        <text x="260" y="440" fill={JE.fg2} fontSize="9" fontFamily="Inter" textAnchor="middle" letterSpacing="1.5">
          REAL-TIME / 6 DIMENSIONS / BUSINESS-VERIFIED
        </text>
      </svg>
      <div className="je-vendor-mobile-scorecard">
        <span>Live room scorecard</span>
        <div className="je-vendor-mobile-track">
          {vendors.slice(0, 2).map((vendor, vendorIndex) => {
            const winner = vendorIndex === 0;
            return (
              <article key={vendor} className={winner ? 'is-winner' : undefined}>
                <div>
                  <strong>{vendor}</strong>
                  <small>{winner ? 'Recommended' : 'Evaluating'}</small>
                </div>
                <em>{totals[vendorIndex]}</em>
                <div>
                  {dims.map((dimension, dimIndex) => (
                    <i key={`${vendor}-${dimension}`}>
                      <span>{dimension}</span>
                      <b style={{ width: `${Math.round(scores[vendorIndex][dimIndex] * 100)}%` }} />
                    </i>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </>
  );
}

function JEVendorEvalSection({ motion = 'full' }: { motion?: Motion }) {
  const checks = [
    ['Business-fit', 'Does it match how your team actually works?'],
    ['Data-truth', 'Where does the model see your data - really?'],
    ['Failure mode', 'What does this look like when it goes wrong?'],
    ['Run-cost', 'TCO at production load, with your volumes.'],
    ['Stack-fit', 'Can it integrate cleanly without creating another system to manage?'],
    ['Adoption path', 'Will users trust it, use it, and know when not to?'],
  ];

  return (
    <section id="vendor-eval" className="je-section je-section-vendor">
      <div className="je-container je-vendor-grid">
        <div>
          <div className="je-kicker">AI vendor & systems evaluation / RFP</div>
          <h2 className="je-section-title">
            Six questions every{' '}
            <br />
            <em>AI vendor</em>{' '}
            should answer.
          </h2>
          <p>
            We score vendors live in the room - not on slides. Business decision makers leave knowing exactly which
            claims held up, where the magic actually is versus the flashy demo, and what is worth taking forward.
          </p>
          <p>
            You get your own expert in the room, working for your strategy and interests - with experience leading RFP
            and Clear Choice decisions for multi-national and local institutions.
          </p>
          <div className="je-eval-checks">
            {checks.map(([title, body]) => (
              <div key={title}>
                <strong>{title}</strong>
                <span>{body}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="je-vendor-panel">
          <JEVendorEval motion={motion} />
        </div>
      </div>
    </section>
  );
}

function JEAICoaching() {
  const topics = [
    {
      code: 'LLM / AGENTS',
      title: 'Executive calls',
      body: 'How to think about AI, where it changes the work, and where it is theatre.',
      signal: 'Judgement',
    },
    {
      code: 'BUY / BUILD',
      title: 'Buy / build / stop',
      body: 'Decision frameworks for vendors, internal builds, pilots, and dead ends.',
      signal: 'Choice',
    },
    {
      code: 'RAG / DATA',
      title: 'Team adoption',
      body: 'Practical sessions that help teams use AI with judgement, pace, and guardrails.',
      signal: 'Adoption',
    },
    {
      code: 'OPS / CONTROL',
      title: 'Operating change',
      body: 'The roles, cadence, controls, and habits that make AI stick after the workshop.',
      signal: 'Rhythm',
    },
  ];

  return (
    <section id="ai-coaching" className="je-section">
      <div className="je-container je-coaching-grid">
        <div>
          <div className="je-kicker">AI coaching</div>
          <h2 className="je-section-title">
            Make sharper AI calls
            <br />
            <em>from LLMs and agents to operating change</em>
          </h2>
          <p>
            Work with leaders - one-to-one or in groups - who need enough technical fluency to challenge LLMs, agents,
            RAG, automation, vendors, and internal builds without getting lost in the tooling.
          </p>
          <p>
            The aim is not surface-level AI literacy. It is better judgement, practical adoption, and confidence in the
            next decision.
          </p>
        </div>
        <div className="je-coaching-console">
          <div className="je-coaching-console-head">
            <span>AI decision stack</span>
            <strong>Technical enough to challenge. Operational enough to ship.</strong>
          </div>
          <div className="je-coaching-stack">
            {topics.map((topic, index) => (
              <article key={topic.title}>
                <span>{topic.code}</span>
                <div>
                  <strong>{topic.title}</strong>
                  <p>{topic.body}</p>
                </div>
                <em>{topic.signal}</em>
                <i style={{ transform: `scaleX(${0.42 + index * 0.14})` }} aria-hidden="true" />
              </article>
            ))}
          </div>
          <div className="je-coaching-trace" aria-hidden="true">
            <span>Model</span>
            <span>Workflow</span>
            <span>Control</span>
            <span>Adoption</span>
          </div>
        </div>
        <div className="je-ai-theatre-panel">
          <div>
            <strong>Not AI theatre.</strong>
            <p>
              AI only matters when it changes how decisions move, work runs, customers are served, risk is controlled,
              and people adopt the new rhythm.
            </p>
          </div>
          <p>
            That is the COO lens: strategy into operating model, technology into workflow, and ambition into outcomes
            someone can actually own.
          </p>
        </div>
      </div>
    </section>
  );
}

function OutcomeRow({
  idx,
  tag,
  title,
  result,
  role,
}: {
  idx: number;
  tag: string;
  title: string;
  result: string;
  role: string;
}) {
  const [setRef, inView] = useInView();
  const [hover, setHover] = useState(false);

  return (
    <article
      ref={setRef}
      className="je-outcome-row"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 800ms ${idx * 100}ms cubic-bezier(0.22,1,0.36,1)`,
      }}
    >
      <div className="je-outcome-rule" style={{ transform: hover ? 'scaleX(1)' : 'scaleX(0)' }} />
      <span>0{idx + 1}</span>
      <small>{tag}</small>
      <div>
        <h3 style={{ color: hover ? JE.gold : JE.cream }}>{title}</h3>
        <p>{result}</p>
      </div>
      <strong>{role}</strong>
    </article>
  );
}

function JEOutcomes() {
  const cases = [
    {
      tag: 'HSBC GCS / 53 markets',
      title: 'Expense, payments and authority simplification',
      result:
        'Standardised expenses, payments and approvals across 53 markets, used AI to deliver $15m annual savings, cut thousands of hours of manual processing, and closed an external audit point.',
      role: 'Strategy & transformation',
    },
    {
      tag: 'Global transformation',
      title: 'Operating-model redesign',
      result:
        'Designed and embedded a target operating model reducing 22 transformation teams to 12, moving 50% to lower-cost regions, reducing management by 30%, and enabling $250m annual savings.',
      role: 'Transformation director',
    },
    {
      tag: '$1.7b procurement',
      title: 'Vendor consolidation and RFP',
      result:
        'Led procurement centralisation across 7 value streams, reduced third-party vendors by 97%, unlocked $400m OpEx reduction, and advised a travel RFP with $5m annual savings and a 6-month execution plan.',
      role: 'COO / advisor',
    },
    {
      tag: 'Compliance transformation',
      title: 'Regulatory technology platform',
      result:
        'Built an 80+ person cross-functional delivery model and shipped five cloud-hosted systems, including AI horizon scanning and dynamic links between regulation, policy and controls.',
      role: 'Programme director',
    },
    {
      tag: 'UK COO office',
      title: 'Operating plan and performance lift',
      result:
        'Owned the $280m annual operating plan, secured ExCo approval for a 5-year strategy, delivered $40m year-one savings, and improved performance by 14%.',
      role: 'Chief of staff',
    },
    {
      tag: 'Retail transformation',
      title: 'Customer experience system',
      result:
        'Created an award-nominated customer experience platform: 20% revenue growth, 170% digital enablement, 600,000 more customer-facing hours, and an 8-place CMA satisfaction improvement.',
      role: 'Programme director',
    },
  ];

  return (
    <section id="outcomes" className="je-section">
      <div className="je-container">
        <div className="je-outcomes-heading">
          <div>
            <div className="je-kicker">Selected outcomes</div>
            <h2 className="je-section-title">What stayed.</h2>
          </div>
          <p>Selected proof from COO, transformation, advisory and AI delivery roles. References available on request.</p>
        </div>
        <div className="je-outcome-list">
          {cases.map((item, index) => (
            <OutcomeRow key={item.title} idx={index} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
}

function JEAbout({ headshot }: { headshot: string }) {
  return (
    <section id="about" className="je-section je-section-muted">
      <div className="je-container je-about-grid">
        <figure className="je-about-photo">
          <img src={headshot} alt="Jon Edgar" />
          <figcaption>Plate II - Jon Edgar</figcaption>
        </figure>
        <div>
          <div className="je-kicker">About</div>
          <h2 className="je-section-title">
            Operator first.
            <br />
            <em>Advisor by trade.</em>
          </h2>
          <div className="je-about-copy">
            <p>
              Fifteen years inside transformation programmes and operating roles - from PMO bench to programme lead,
              COO, and trusted board advisor. Cross-border by default, technical when it matters, plain-spoken always.
            </p>
            <p>
              The work has held up because the operating models did. That&apos;s the whole bet, and it&apos;s what every
              engagement is built around.
            </p>
          </div>
          <div className="je-about-facts">
            <div>
              <strong>Sectors</strong>
              <span>Retail / Industrial / Financial services / Public</span>
            </div>
            <div>
              <strong>Engagement</strong>
              <span>Permanent COO / Interim transformation / One-off advisory</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function JEField({
  label,
  value,
  onChange,
  type = 'text',
  textarea = false,
  rows = 5,
  optional = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  textarea?: boolean;
  rows?: number;
  optional?: boolean;
}) {
  const [focus, setFocus] = useState(false);

  return (
    <label className={`je-field ${focus ? 'is-focused' : ''}`}>
      <span>
        {label}
        {optional && <> <em>(Optional)</em></>}
      </span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={rows}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
        />
      )}
    </label>
  );
}

function JEEnquiry() {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState({
    name: '',
    email: '',
    telephone: '',
    org: '',
    message: '',
    companyWebsite: '',
  });

  const update = (key: keyof typeof data, value: string) => {
    setData((current) => ({ ...current, [key]: value }));
  };

  const valid =
    data.name.trim().length > 1 &&
    data.email.includes('@') &&
    data.telephone.trim().length > 5 &&
    data.message.trim().length > 8 &&
    data.companyWebsite.length === 0;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!valid) return;
    setStatus('sending');

    const payload = {
      site: 'je.consulting',
      destination: 'jon@je.consulting',
      name: data.name.trim(),
      email: data.email.trim(),
      telephone: data.telephone.trim(),
      organisation: data.org.trim(),
      message: data.message.trim(),
      sourcePath: window.location.pathname,
      honeypot: data.companyWebsite,
    };

    try {
      const response = await fetch('/api/enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setStatus('sent');
      setData({
        name: '',
        email: '',
        telephone: '',
        org: '',
        message: '',
        companyWebsite: '',
      });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="enquire" className="je-section">
      <div className="je-container je-enquiry-grid">
        <div>
          <div className="je-kicker">Contact</div>
          <h2 className="je-section-title">Get in touch</h2>
          <p>
            Whether it is a COO role, transformation mandate, AI evaluation, or coaching need - or just to connect -
            I&apos;ll reply within one working day.
          </p>
          <div className="je-direct-block">
            <div>
              <strong>Direct</strong>
              <a href="mailto:jon@je.consulting">jon@je.consulting</a>
            </div>
            <div>
              <strong>Based</strong>
              <span>United Kingdom / works globally</span>
            </div>
          </div>
        </div>

        <form className="je-form" onSubmit={submit}>
          {status === 'sent' ? (
            <div className="je-sent-panel">
              <strong>Thank you.</strong>
              <span>Enquiry received. I&apos;ll reply within one working day with how I can help.</span>
            </div>
          ) : (
            <>
              <JEField label="Name" value={data.name} onChange={(value) => update('name', value)} />
              <JEField label="Email" type="email" value={data.email} onChange={(value) => update('email', value)} />
              <JEField
                label="Telephone"
                type="tel"
                value={data.telephone}
                onChange={(value) => update('telephone', value)}
              />
              <JEField label="Organisation" value={data.org} onChange={(value) => update('org', value)} optional />
              <JEField
                label="How can I help"
                value={data.message}
                onChange={(value) => update('message', value)}
                textarea
              />
              <label className="je-honeypot" aria-hidden="true">
                Website
                <input
                  tabIndex={-1}
                  value={data.companyWebsite}
                  onChange={(event) => update('companyWebsite', event.target.value)}
                  autoComplete="off"
                />
              </label>
              <div className="je-form-footer">
                <button type="submit" disabled={!valid || status === 'sending'} className="je-btn-primary">
                  {status === 'sending' ? 'Sending' : 'Send enquiry'}
                </button>
              </div>
              {status === 'error' && (
                <p className="je-form-error">
                  The form endpoint is not available in this environment. Email jon@je.consulting instead.
                </p>
              )}
            </>
          )}
        </form>
      </div>
    </section>
  );
}

function JEFooter() {
  return (
    <footer className="je-footer">
      <div className="je-container">
        <div className="je-footer-grid">
          <div>
            <div className="je-footer-brand">
              <span>JE</span>
              <strong>.consulting</strong>
            </div>
            <p>Independent advisory practice. Operating-model transformation, executive coaching, practical AI.</p>
          </div>
          <div>
            <strong>Practice</strong>
            <span>Coaching</span>
            <span>Practical AI</span>
            <span>Op-model</span>
            <span>PMO</span>
          </div>
          <div>
            <strong>Proof</strong>
            <span>Approach</span>
            <span>Outcomes</span>
            <span>CV / dossier</span>
            <span>Contact</span>
          </div>
          <div>
            <strong>Direct</strong>
            <span>jon@je.consulting</span>
            <span>United Kingdom / global</span>
          </div>
        </div>
        <div className="je-footer-bottom">
          <span>JE.Consulting / {new Date().getFullYear()}</span>
          <a href="https://vibecoo.ai">Sister practice / vibecoo.ai</a>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <>
      <HashScroller />
      <JEParticleField intensity={MOTION} />
      <div className="je-page">
        <JENav />
        <main>
          <JEHero headshot={heroHeadshot} motion={MOTION} />
          <SectionCard>
            <JELeadership />
          </SectionCard>
          <SectionCard>
            <JEOpModel motion={MOTION} />
          </SectionCard>
          <SectionCard>
            <JEVendorEvalSection motion={MOTION} />
          </SectionCard>
          <SectionCard>
            <JEAICoaching />
          </SectionCard>
          <SectionCard>
            <JEEnquiry />
          </SectionCard>
        </main>
        <JEFooter />
      </div>
    </>
  );
}
